import { useCallback, useEffect, useRef, useState } from 'react';
import { getAnalyticsTodos, updateAnalyticsTodos } from '../api';

export const TODO_STORAGE_KEY = 'cvflow_todos';

export const INITIAL_TODOS = [
  { id: 't1', title: 'Anschreiben fertigstellen', due: 'Heute', pri: 'urgent', done: false },
  { id: 't2', title: 'Portfolio-PDF aktualisieren', due: 'Heute', pri: 'amber', done: false },
  { id: 't3', title: 'Follow-up Email schreiben', due: 'Morgen', pri: 'normal', done: false },
];

function normalizeTodos(value) {
  return Array.isArray(value)
    ? value
        .filter(todo => todo && typeof todo.title === 'string')
        .map(todo => ({
          id: String(todo.id || `t_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`),
          title: todo.title,
          due: todo.due || 'Offen',
          pri: todo.pri || 'normal',
          done: Boolean(todo.done),
        }))
    : [];
}

function loadLocalTodos(fallback) {
  try {
    const raw = localStorage.getItem(TODO_STORAGE_KEY);
    if (raw !== null) return normalizeTodos(JSON.parse(raw));
  } catch {}
  return fallback;
}

function saveLocalTodos(todos) {
  try { localStorage.setItem(TODO_STORAGE_KEY, JSON.stringify(todos)); } catch {}
}

export function useAnalyticsTodos({ enabled = true, fallback = INITIAL_TODOS } = {}) {
  const [todos, setTodos] = useState(() => loadLocalTodos(fallback));
  const lastLocalWriteRef = useRef(0);

  const persistTodos = useCallback((next) => {
    const normalized = normalizeTodos(next);
    lastLocalWriteRef.current = Date.now();
    saveLocalTodos(normalized);
    if (enabled) updateAnalyticsTodos(normalized).catch(() => {});
    return normalized;
  }, [enabled]);

  const setSyncedTodos = useCallback((nextOrUpdater) => {
    setTodos(prev => {
      const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater;
      return persistTodos(next);
    });
  }, [persistTodos]);

  const refreshTodos = useCallback(async () => {
    if (!enabled) return;
    if (Date.now() - lastLocalWriteRef.current < 3000) return;
    try {
      const res = await getAnalyticsTodos();
      if (Date.now() - lastLocalWriteRef.current < 3000) return;
      const remote = normalizeTodos(res.data?.todos);
      setTodos(remote);
      saveLocalTodos(remote);
    } catch {}
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return undefined;
    refreshTodos();
    const onFocus = () => refreshTodos();
    const onVisible = () => {
      if (!document.hidden) refreshTodos();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, refreshTodos]);

  return { todos, setTodos: setSyncedTodos, refreshTodos };
}
